import React, { useState, useEffect } from 'react';
import { Card, Button, Select, Modal, Form, Input, message, Space } from 'antd';
import AdaptiveTable from '../components/AdaptiveTable';
import { supplierApi, type SupplierDTO, type SupplierContactDTO } from '../api/kfc';

const SupplierContacts: React.FC = () => {
  const [suppliers, setSuppliers] = useState<SupplierDTO[]>([]);
  const [selectedCode, setSelectedCode] = useState<string | undefined>();
  const [contacts, setContacts] = useState<SupplierContactDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<(SupplierContactDTO & { _index?: number }) | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    supplierApi
      .list()
      .then((res) => setSuppliers(res.data))
      .catch(() => {
        setSuppliers([]);
        message.warning('无法加载供应商列表');
      });
  }, []);

  useEffect(() => {
    if (!selectedCode) {
      setContacts([]);
      return;
    }
    setLoading(true);
    supplierApi
      .getByCode(selectedCode)
      .then((res) => setContacts(res.data.contacts || []))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, [selectedCode]);

  const openAdd = () => {
    setEditingContact(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (c: SupplierContactDTO, index: number) => {
    setEditingContact({ ...c, _index: index });
    form.setFieldsValue(c);
    setModalOpen(true);
  };

  const onFinish = () => {
    if (!selectedCode) return;
    form.validateFields().then((values) => {
      const sup = suppliers.find((s) => s.supplierId === selectedCode);
      if (!sup?.id) return;
      const list = [...(contacts || [])];
      const idx = editingContact?._index;
      const newContact: SupplierContactDTO = {
        contactName: values.contactName,
        phoneNumber: values.phoneNumber,
        remark: values.remark,
      };
      if (typeof idx === 'number' && idx >= 0) {
        list[idx] = { ...list[idx], ...newContact };
      } else {
        list.push(newContact);
      }
      supplierApi
        .update(sup.id, { ...sup, contacts: list })
        .then(() => {
          message.success('保存成功');
          setModalOpen(false);
          supplierApi.getByCode(selectedCode).then((r) => setContacts(r.data.contacts || []));
        })
        .catch((e) => message.error(e.response?.data?.message || '保存失败'));
    });
  };

  const doRemove = (index: number) => {
    const sup = suppliers.find((s) => s.supplierId === selectedCode);
    if (!sup?.id) return;
    const list = contacts.filter((_, i) => i !== index);
    supplierApi
      .update(sup.id, { ...sup, contacts: list })
      .then(() => {
        message.success('已删除');
        setContacts(list);
      })
      .catch((e) => message.error(e.response?.data?.message || '删除失败'));
  };

  const cols = [
    { title: '联系人', dataIndex: 'contactName', width: 120 },
    { title: '电话', dataIndex: 'phoneNumber', width: 140 },
    { title: '备注', dataIndex: 'remark', width: 180, ellipsis: true },
    {
      title: '操作',
      width: 120,
      render: (_: unknown, __: SupplierContactDTO, index: number) => (
        <Space>
          <Button type="link" size="small" onClick={() => openEdit(contacts[index], index)}>
            编辑
          </Button>
          <Button type="link" size="small" danger onClick={() => doRemove(index)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card title="供应商联系人">
        <Space style={{ marginBottom: 16 }}>
          <Select
            placeholder="选择供应商"
            allowClear
            style={{ width: 280 }}
            options={suppliers.map((s) => ({
              label: `${s.supplierId} - ${s.supplierName}`,
              value: s.supplierId,
            }))}
            value={selectedCode}
            onChange={setSelectedCode}
          />
          {selectedCode && (
            <Button type="primary" onClick={openAdd}>
              新增联系人
            </Button>
          )}
        </Space>
        <AdaptiveTable
          loading={loading}
          dataSource={contacts}
          rowKey={(_, i) => String(i)}
          columns={cols}
          pagination={false}
        />
      </Card>
      <Modal
        title={editingContact ? '编辑联系人' : '新增联系人'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onFinish}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="contactName" label="联系人姓名" rules={[{ required: true, message: '请输入联系人姓名' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phoneNumber" label="电话">
            <Input />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default SupplierContacts;
